import { AiTag, NewTag } from '../tag';
import { Icon } from '../icon';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useRelayStore } from 'src/relay';
import { getPathName } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { GetIcon } from './IconManager';
import { IsSubMenu, ItemMenu } from './Menu';
import { MenuItemsProps } from './TreeMenu';
import { T, isMenuItemSelected } from './helpers';
import {
    Chip,
    ChipAdd,
    ChipContainer,
    ChipLabel,
    ChipPickerItem,
    ChipPickerPopup,
    ChipPickerWrap,
    ChipRemoveButton,
    ContainerArrow,
    ContainerIcon,
    LinkSidebar,
    MenuBottomDivider,
    MenuDivider,
    MenuSectionArrow,
    MenuSectionBody,
    MenuSectionHeader,
    Navbar,
    NavbarContainer,
    NavbarLi
} from './leftAppSidebarStyles';
import SidebarPortalPopup from './SidebarPortalPopup';

export const SelectedMenuItem = styled.span`
    color: ${theme.app.secondary};
`;

const isAllowed = (iamUserPermissions: string[], iamUser: boolean, permissionsToHave: string[]) => {
    if (!iamUser || permissionsToHave.length === 0) {
        return true;
    }

    return permissionsToHave.every((p) => iamUserPermissions.includes(p));
};

const MenuList = (itemMenu: ItemMenu, goToMenu: (item: ItemMenu) => any, iamUser: boolean, iamUserPermissions: string[]) => {
    return itemMenu.menu.map((item, i) => (
        <div key={i}>
            <ContainerLi itemMenu={item} isHeader={false} goToMenu={goToMenu} disabled={!isAllowed(iamUserPermissions, iamUser, item.permissions)} />
        </div>
    ));
};

interface ContainerLiProps {
    itemMenu: ItemMenu;
    isHeader: boolean;
    goToMenu: (item: ItemMenu) => any;
    disabled?: boolean;
    onBeforeNavigate?: () => void;
}

const ContainerLi: FunctionComponent<ContainerLiProps> = ({ itemMenu, isHeader, goToMenu, onBeforeNavigate }) => {
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();

    const intelNav = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        if (!isHeader && IsSubMenu(itemMenu) && e.currentTarget.target !== '_blank') {
            goToMenu(itemMenu);
            return;
        }

        onBeforeNavigate?.();

        // Emit custom event to allow other components to intercept navigation
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(itemMenu.urlPage),
                urlPage: itemMenu.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });

        const allowed = window.dispatchEvent(navigationEvent);

        // If the event was not prevented, proceed with navigation
        if (allowed) {
            if (isDirty) incrementStoreKey();
            // itemMenu.urlPage is absolute (it comes from the backend treeMenu). When the user is
            // inside DAM we still want a full reload — the React tree must reset to unmount the
            // DAM iframe — but we must navigate via the current origin's pathname, otherwise on
            // localhost (or staging) we'd jump to the production host embedded in the URL.
            const pathname = getPathName(itemMenu.urlPage);
            if (window.location.href.toLowerCase().includes('dam')) window.location.href = pathname;
            else navigate(pathname);
        }
    };

    const handleAuxClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (e.button == 1) {
            // Middle click
            e.preventDefault();
            window.open(itemMenu.urlPage, '_blank');
            return;
        }
    };

    const isSelected = isMenuItemSelected(itemMenu);

    return (
        <NavbarLi $isVisible={itemMenu.name.toLowerCase() !== 'dam'}>
            <LinkSidebar
                href={isHeader ? 'javascript:void(0)' : itemMenu.urlPage}
                $isHeader={isHeader}
                onClick={intelNav}
                onAuxClick={handleAuxClick}
                target={itemMenu.isExternalUrl ? '_blank' : '_self'}
            >
                <ContainerIcon $isSelected={isSelected}>{GetIcon(itemMenu.icon) && React.createElement(GetIcon(itemMenu.icon)!)}</ContainerIcon>

                {isSelected ? <SelectedMenuItem>{itemMenu.name}</SelectedMenuItem> : <span>{itemMenu.name}</span>}

                {itemMenu.isAI && <AiTag>AI</AiTag>}
                {itemMenu.isNew && <NewTag>New</NewTag>}
                <ContainerArrow $isHidden={IsSubMenu(itemMenu) && !isHeader}>
                    <Icon>{GetIcon('angle_right_solid') && React.createElement(GetIcon('angle_right_solid')!)}</Icon>
                </ContainerArrow>
            </LinkSidebar>
        </NavbarLi>
    );
};

export const BackContainer = styled.div`
    cursor: pointer;
    padding: 5px;
    display: flex;
    align-items: center;
    gap: 10px;

    &:hover {
        opacity: 0.7;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`;

// ── Main menu categorization (HOME level) ───────────────────────────
// The main sidebar groups items into collapsible categories, inspired
// by the product sidebar's CollapsibleSection pattern. Items are
// identified by URL patterns so their original server-provided names
// (and localizations) are preserved.

const flattenMenuItems = (root: ItemMenu): ItemMenu[] => {
    const result: ItemMenu[] = [];
    const walk = (items: ItemMenu[]) => {
        for (const item of items) {
            result.push(item);
            if (item.menu && item.menu.length > 0) walk(item.menu);
        }
    };
    walk(root.menu || []);
    return result;
};

// Finds a menu item by trying the pattern against both the urlPage and the
// server-provided regex field (which the backend uses to identify the item
// and is more stable across URL format variations).
const findByUrl = (items: ItemMenu[], pattern: RegExp): ItemMenu | undefined =>
    items.find((item) => (!!item.urlPage && pattern.test(item.urlPage)) || (!!item.regex && pattern.test(item.regex)));

type MenuBlock =
    | { kind: 'item'; item: ItemMenu }
    | { kind: 'category'; key: string; label: string; items: ItemMenu[]; defaultOpen: boolean }
    | { kind: 'category-link'; item: ItemMenu };

interface CategorizedMenu {
    blocks: MenuBlock[];
    bottomItems: ItemMenu[];
}

const buildCategorizedMenu = (root: ItemMenu): CategorizedMenu => {
    const all = flattenMenuItems(root);
    const pick = (pattern: RegExp) => findByUrl(all, pattern);
    const notNull = <T,>(x: T | undefined | null): x is T => x != null;

    // Individual items
    // Home is not listed in the menu — the logo and the Home icon in the
    // sidebar Header are the entry points, saving a row.
    const products = pick(/\/Admin\/Products(?:\?|$|\/$)/i);
    const toolsParent = pick(/\/Admin\/Tools(?:\?|$|\/$)/i);
    const settingsParent = pick(/\/Admin\/Settings(?:\?|$|\/$)/i);

    // Category items — patterns are intentionally lenient (hyphen/underscore/space
    // variants, case-insensitive, no leading slash) so they match both the urlPage
    // and the server-provided regex field across environments.
    const imagesPattern = /\bimages\b|image[-_\s]?clipart|clipart[-_\s]?galler/i;
    const imagesItem = pick(imagesPattern);
    // Display label override: server sends "Images and clipart galleries" but we
    // want the shorter "Image gallery" in the sidebar (both in Quick Access and
    // Libraries). Non-destructive: we clone the item, the original is untouched.
    const imagesDisplay: ItemMenu | undefined = imagesItem ? { ...imagesItem, name: T._('Image gallery', 'Admin') } : undefined;

    const preview3dItem = pick(/preview[-_\s]?3d|3d[-_\s]?preview/i);
    const preview3dDisplay: ItemMenu | undefined = preview3dItem ? { ...preview3dItem, name: T._('3D & AR', 'Admin') } : undefined;

    const templatesItem = pick(/\/tools\/template|template[-_\s]?product|pre[-_\s]?designed[-_\s]?template/i);
    const templatesDisplay: ItemMenu | undefined = templatesItem ? { ...templatesItem, name: T._('Templates', 'Admin') } : undefined;

    // Printing methods and Pricing rules keep their original (server) names.
    const quickAccess = [pick(/printing[-_\s]?method/i), imagesDisplay, templatesDisplay, pick(/\/pricings\b|pricing[-_\s]?rule/i), preview3dDisplay].filter(
        notNull
    );

    // Output (Order Settings → existing "Order's summary" / Pdf-Summary)
    const output = [
        pick(/\/admin\/orders(?:\?|$|\/$)|\.\*orders/i),
        pick(/pdf[-_\s]?summary|summary[-_\s]?preview|order[-_\s]?summary/i),
        pick(/draft[-_\s]?designs?|saved[-_\s]?designs?/i)
    ].filter(notNull);

    // AI Creative Tools — behaves like Tools/Settings: category-style label that
    // navigates directly to the dedicated AI page. If the backend already exposes
    // an AI parent entry in the tree we reuse it; otherwise we fall back to a
    // synthetic item with a best-guess route (tune the URL if your backend differs).
    const aiCreativeFromServer = pick(/ai[-_\s]?creative|creative[-_\s]?ai|\/admin\/ai[-_\s]/i);
    const aiCreativeLink: ItemMenu = aiCreativeFromServer || {
        name: T._('AI Creative Tools', 'Admin'),
        urlPage: '/en/Admin/AI-Creative-Tools',
        isExternalUrl: false,
        icon: '',
        tooltip: '',
        helpKeywords: [],
        menu: [],
        isNew: false,
        isAI: false,
        permissions: []
    };

    // Libraries
    const libraries = [
        pick(/\/tools\/shapes|shapes[-_\s]?and[-_\s]?masks?|\bshapes\b/i),
        pick(/\/tools\/fonts\b|\bfonts\b/i),
        imagesDisplay,
        pick(/pantone/i)
    ].filter(notNull);

    // Sales Modules
    const salesModules = [pick(/\/tools\/get[-_\s]?a[-_\s]?quote|get[-_\s]?a[-_\s]?quote/i), pick(/\/sales[-_\s]?channels?|\(?\^channels\)?/i)].filter(notNull);

    const blocks: MenuBlock[] = [];
    if (products) blocks.push({ kind: 'item', item: products });
    if (quickAccess.length > 0)
        blocks.push({ kind: 'category', key: 'quick-access', label: T._('Quick Access', 'Admin'), items: quickAccess, defaultOpen: true });
    if (output.length > 0) blocks.push({ kind: 'category', key: 'output', label: T._('Output', 'Admin'), items: output, defaultOpen: true });
    blocks.push({ kind: 'category-link', item: aiCreativeLink });
    if (libraries.length > 0) blocks.push({ kind: 'category', key: 'libraries', label: T._('Libraries', 'Admin'), items: libraries, defaultOpen: false });
    // Tools and Settings render as category-style labels that navigate directly
    // to their page (no collapsible submenu — the pages themselves list all children)
    if (toolsParent) blocks.push({ kind: 'category-link', item: toolsParent });
    if (settingsParent) blocks.push({ kind: 'category-link', item: settingsParent });
    if (salesModules.length > 0)
        blocks.push({ kind: 'category', key: 'sales-modules', label: T._('Sales Channels & Modules', 'Admin'), items: salesModules, defaultOpen: false });

    // Fallback: any top-level items we didn't place (e.g. in DAM mode)
    const placedUrls = new Set<string>();
    for (const b of blocks) {
        if (b.kind === 'item' || b.kind === 'category-link') placedUrls.add(b.item.urlPage);
        else for (const i of b.items) placedUrls.add(i.urlPage);
    }
    for (const item of root.menu || []) {
        if (placedUrls.has(item.urlPage)) continue;
        const nameLc = item.name.toLowerCase();
        if (nameLc === 'dam' || nameLc === 'home') continue;
        blocks.push({ kind: 'item', item });
    }

    return {
        blocks,
        bottomItems: []
    };
};

// Compact chip-style item used inside Quick Access: same navigation
// behaviour as ContainerLi but rendered as an inline pill with the icon.
const ChipItem: FunctionComponent<{ item: ItemMenu; onBeforeNavigate?: () => void }> = ({ item, onBeforeNavigate }) => {
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();
    const isSelected = isMenuItemSelected(item);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        onBeforeNavigate?.();
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(item.urlPage),
                urlPage: item.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });
        const allowed = window.dispatchEvent(navigationEvent);
        if (allowed) {
            if (isDirty) incrementStoreKey();
            if (window.location.href.toLowerCase().includes('dam')) window.open(item.urlPage, '_self');
            else navigate(getPathName(item.urlPage));
        }
    };

    return (
        <Chip href={item.urlPage} $isSelected={isSelected} onClick={handleClick}>
            <ChipLabel>{item.name}</ChipLabel>
        </Chip>
    );
};

// Custom chips in Quick Access: the user can pin multiple extra tools.
// "+ Add" is always visible; each pinned chip has an × to remove it.
// Choices are persisted as a JSON array in localStorage.
const CUSTOM_CHIPS_STORAGE_KEY = 'sidebar-quick-access-custom-urls';

const CustomChips: FunctionComponent<{
    allItems: ItemMenu[];
    excludedUrls: Set<string>;
    onBeforeNavigate?: () => void;
}> = ({ allItems, excludedUrls, onBeforeNavigate }) => {
    const [customUrls, setCustomUrls] = useState<string[]>(() => {
        try {
            const raw = localStorage.getItem(CUSTOM_CHIPS_STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
        } catch {
            return [];
        }
    });
    const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
    const addRef = useRef<HTMLButtonElement>(null);

    const openPicker = () => {
        const rect = addRef.current?.getBoundingClientRect();
        if (rect) setPickerPos({ top: rect.bottom + 4, left: rect.left });
    };

    const persist = (urls: string[]) => {
        try {
            localStorage.setItem(CUSTOM_CHIPS_STORAGE_KEY, JSON.stringify(urls));
        } catch {
            /* noop */
        }
        setCustomUrls(urls);
    };

    const addCustom = (url: string) => persist([...customUrls, url]);
    const removeCustom = (url: string) => persist(customUrls.filter((u) => u !== url));

    const alreadyPinned = new Set(customUrls);
    const pickerOptions = allItems
        .filter(
            (item) =>
                !!item.urlPage &&
                item.menu.length === 0 &&
                !excludedUrls.has(item.urlPage) &&
                !alreadyPinned.has(item.urlPage) &&
                item.name.toLowerCase() !== 'home' &&
                item.name.toLowerCase() !== 'dam'
        )
        .filter((item, index, arr) => arr.findIndex((x) => x.urlPage === item.urlPage) === index);

    const pinnedItems = customUrls.map((url) => allItems.find((item) => item.urlPage === url)).filter((x): x is ItemMenu => !!x);

    return (
        <>
            {pinnedItems.map((item) => (
                <ChipPickerWrap key={item.urlPage}>
                    <ChipItem item={item} onBeforeNavigate={onBeforeNavigate} />
                    <ChipRemoveButton
                        onClick={(e) => {
                            e.stopPropagation();
                            removeCustom(item.urlPage);
                        }}
                        title={T._('Remove from Quick Access', 'Admin')}
                    >
                        ×
                    </ChipRemoveButton>
                </ChipPickerWrap>
            ))}
            <ChipAdd ref={addRef} onClick={openPicker} type='button'>
                + {T._('Add', 'Admin')}
            </ChipAdd>

            <SidebarPortalPopup open={pickerPos !== null} onClose={() => setPickerPos(null)} style={pickerPos ?? undefined}>
                <ChipPickerPopup style={{ position: 'relative', top: 0, left: 0, right: 'auto', width: 240 }}>
                    {pickerOptions.map((item) => (
                        <ChipPickerItem
                            key={item.urlPage}
                            onClick={() => {
                                addCustom(item.urlPage);
                                setPickerPos(null);
                            }}
                        >
                            {item.name}
                        </ChipPickerItem>
                    ))}
                    {pickerOptions.length === 0 && <ChipPickerItem style={{ color: '#999', cursor: 'default' }}>{T._('No options', 'Admin')}</ChipPickerItem>}
                </ChipPickerPopup>
            </SidebarPortalPopup>
        </>
    );
};

// A category-style label that navigates directly (no expansion).
// Used for Tools, Settings and AI Creative Tools: they read as categories but
// go to their dedicated page on click. When navigating we also collapse any
// currently-open category so the sidebar state stays consistent.
const CategoryLink: FunctionComponent<{ item: ItemMenu; onBeforeNavigate?: () => void }> = ({ item, onBeforeNavigate }) => {
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const navigate = useNavigate();
    // Only highlight when the current URL matches the category page exactly
    // (e.g. /Admin/Tools) — not when on a child page (e.g. /tools/get-a-quote).
    const currentPath = getPathName(window.location.href).toLowerCase().replace(/\/$/, '');
    const itemPath = getPathName(item.urlPage).toLowerCase().replace(/\/$/, '');
    const isSelected = currentPath === itemPath;

    const handleClick = () => {
        onBeforeNavigate?.();
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(item.urlPage),
                urlPage: item.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });
        const allowed = window.dispatchEvent(navigationEvent);
        if (allowed) {
            if (isDirty) incrementStoreKey();
            if (window.location.href.toLowerCase().includes('dam')) window.open(item.urlPage, '_self');
            else navigate(getPathName(item.urlPage));
        }
    };

    return (
        <MenuSectionHeader onClick={handleClick} $isSelected={isSelected}>
            <span>{item.name}</span>
        </MenuSectionHeader>
    );
};

const SectionedMenu: FunctionComponent<{
    root: ItemMenu;
    goToMenu: (item: ItemMenu) => void;
    iamUser: boolean;
    iamUserPermissions: string[];
}> = ({ root, goToMenu, iamUser, iamUserPermissions }) => {
    // Subscribe to URL changes so `isMenuItemSelected` (which reads
    // window.location.href) is re-evaluated on every navigation.
    useLocation();
    const { blocks, bottomItems } = buildCategorizedMenu(root);

    // Quick Access and Output are pinned-open (no toggle, no arrow) and not
    // tracked in state. Other categories are independently toggleable; on mount,
    // auto-open those that contain the selected page, plus those with defaultOpen.
    const PINNED_OPEN = new Set(['quick-access', 'output']);
    const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(() => {
        const state: Record<string, boolean> = {};
        for (const b of blocks) {
            if (b.kind === 'category' && !PINNED_OPEN.has(b.key)) {
                const hasSelected = b.items.some((item) => isMenuItemSelected(item));
                state[b.key] = b.defaultOpen || hasSelected;
            }
        }
        return state;
    });

    const toggle = (key: string) => {
        if (PINNED_OPEN.has(key)) return;
        setOpenCategories((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const closeAll = () =>
        setOpenCategories((prev) => {
            const next: Record<string, boolean> = {};
            for (const k of Object.keys(prev)) next[k] = false;
            return next;
        });

    const renderItem = (item: ItemMenu, key: string | number) => (
        <div key={key}>
            <ContainerLi
                itemMenu={item}
                isHeader={false}
                goToMenu={goToMenu}
                disabled={!isAllowed(iamUserPermissions, iamUser, item.permissions)}
                onBeforeNavigate={closeAll}
            />
        </div>
    );

    // Dividers are placed between visual groups: first category after top items,
    // and after each pinned-open category (Quick Access, Output).
    const needsDividerBefore = (index: number): boolean => {
        const block = blocks[index];
        const prev = blocks[index - 1];
        if (!block || block.kind === 'item') return false;
        if (!prev || prev.kind === 'item') return true;
        if (prev.kind === 'category' && PINNED_OPEN.has(prev.key)) return true;
        return false;
    };

    return (
        <>
            {blocks.map((block, i) => {
                const divider = needsDividerBefore(i) ? <MenuDivider key={`divider-${i}`} /> : null;
                if (block.kind === 'item') {
                    return renderItem(block.item, `item-${i}`);
                }
                if (block.kind === 'category-link') {
                    return (
                        <React.Fragment key={`cat-link-wrap-${i}`}>
                            {divider}
                            <CategoryLink item={block.item} onBeforeNavigate={closeAll} />
                        </React.Fragment>
                    );
                }
                const isPinned = PINNED_OPEN.has(block.key);
                const isOpen = isPinned || !!openCategories[block.key];
                const isQuickAccess = block.key === 'quick-access';
                return (
                    <React.Fragment key={block.key}>
                        {divider}
                        <MenuSectionHeader onClick={isPinned ? undefined : () => toggle(block.key)} $isSelected={!isPinned && isOpen} $isStatic={isPinned}>
                            <span>{block.label}</span>
                            {!isPinned && <MenuSectionArrow $isOpen={isOpen}>&#9654;</MenuSectionArrow>}
                        </MenuSectionHeader>
                        <MenuSectionBody $collapsed={!isOpen}>
                            {isQuickAccess ? (
                                <ChipContainer>
                                    {block.items.map((item, j) => (
                                        <ChipItem key={`${block.key}-${j}`} item={item} onBeforeNavigate={closeAll} />
                                    ))}
                                    <CustomChips
                                        allItems={flattenMenuItems(root)}
                                        excludedUrls={new Set(block.items.map((i) => i.urlPage))}
                                        onBeforeNavigate={closeAll}
                                    />
                                </ChipContainer>
                            ) : (
                                block.items.map((item, j) => renderItem(item, `${block.key}-${j}`))
                            )}
                        </MenuSectionBody>
                    </React.Fragment>
                );
            })}

            {bottomItems.length > 0 && <MenuBottomDivider>{bottomItems.map((item, i) => renderItem(item, `bottom-${i}`))}</MenuBottomDivider>}
        </>
    );
};

const MenuItems: FunctionComponent<MenuItemsProps> = (props) => {
    const isHome = props.currentMenu.name === 'HOME';

    if (isHome) {
        return (
            <NavbarContainer>
                <Navbar style={{ position: 'relative' }}>
                    <SectionedMenu root={props.currentMenu} goToMenu={props.goToMenu} iamUser={props.iamUser} iamUserPermissions={props.iamUserPermissions} />
                </Navbar>
            </NavbarContainer>
        );
    }

    return (
        <TransitionGroup component={NavbarContainer}>
            <CSSTransition key={props.currentMenu.urlPage} timeout={300} classNames='transition'>
                <Navbar>
                    <BackContainer onClick={props.onBackClick}>
                        <Icon>{React.createElement(GetIcon('arrow_left_solid')!)}</Icon>

                        <strong>{props.homeMenu.name}</strong>
                    </BackContainer>

                    {MenuList(props.currentMenu, props.goToMenu, props.iamUser, props.iamUserPermissions)}
                </Navbar>
            </CSSTransition>
        </TransitionGroup>
    );
};

export default MenuItems;
